export const GALLERY_ASSET_TAG_CODE = 'before_after_gallery';
export const SLIDER_ASSET_TAG_CODE = 'before_after_slider';
export const TAB_ASSET_TAG_CODE = 'before_after_tab';

export const GALLERY_ASSET_TAG_LABEL = 'Before-After Gallery';
export const SLIDER_ASSET_TAG_LABEL = 'Before-After Slider';
export const TAB_ASSET_TAG_LABEL = 'Before-After Tab';

// Built-in tab codes of the Magento module. Each has
// beforeafter_gallery/tabs/<code>_enabled|_label|_image configs and its
// sections live at beforeafter_gallery/data/sections_<code>.
export const GALLERY_TAB_CODES = ['tab1', 'tab2', 'tab3', 'tab4', 'tab5', 'tab6'];

export const GALLERY_TAB_DEFAULT_LABELS = {
  tab1: 'Body',
  tab2: 'Breast',
  tab3: 'Face',
  tab4: 'Skin',
  tab5: 'Injectables',
  tab6: 'Other'
};

export const GALLERY_TAB_DEFAULT_ENABLED = {
  tab1: 1,
  tab2: 1,
  tab3: 1,
  tab4: 0,
  tab5: 0,
  tab6: 0
};

export const GENERAL_CONFIG_PATHS = {
  enabled: 'beforeafter_gallery/general/enabled',
  url_key: 'beforeafter_gallery/general/url_key',
  page_title: 'beforeafter_gallery/general/page_title',
  description: 'beforeafter_gallery/general/description',
  meta_title: 'beforeafter_gallery/general/meta_title',
  meta_description: 'beforeafter_gallery/general/meta_description',
  meta_keywords: 'beforeafter_gallery/general/meta_keywords'
};

export const DISPLAY_CONFIG_PATHS = {
  show_surgeon_filter: 'beforeafter_gallery/display/show_surgeon_filter',
  surgeon_filter_label: 'beforeafter_gallery/display/surgeon_filter_label',
  surgeon_filter_all_label: 'beforeafter_gallery/display/surgeon_filter_all_label',
  show_tag_filter: 'beforeafter_gallery/display/show_tag_filter',
  tag_filter_label: 'beforeafter_gallery/display/tag_filter_label',
  tag_filter_all_label: 'beforeafter_gallery/display/tag_filter_all_label',
  show_attributes: 'beforeafter_gallery/display/show_attributes',
  show_item_titles: 'beforeafter_gallery/display/show_item_titles',
  before_label: 'beforeafter_gallery/display/before_label',
  after_label: 'beforeafter_gallery/display/after_label'
};

export const SURGEONS_CONFIG_PATH = 'beforeafter_gallery/data/surgeons';

export const getTabEnabledConfigPath = (tabCode) => `beforeafter_gallery/tabs/${tabCode}_enabled`;
export const getTabLabelConfigPath = (tabCode) => `beforeafter_gallery/tabs/${tabCode}_label`;
export const getTabImageConfigPath = (tabCode) => `beforeafter_gallery/tabs/${tabCode}_image`;
export const getTabSectionsConfigPath = (tabCode) => `beforeafter_gallery/data/sections_${tabCode}`;

export const MAX_IMAGES_PER_TAB = 500;
// Each tab's sections are stored as one JSON string in core_config_data.value,
// which is a MySQL TEXT column (65,535 bytes) — keep a safety margin
export const MAX_SECTIONS_CONFIG_LENGTH = 60000;

export const countTabImages = (sections) => (sections || []).reduce(
  (total, section) => total + (section.items || []).reduce(
    (itemTotal, item) => itemTotal + (item.images?.length || 0),
    0
  ),
  0
);

export const SUGGESTED_ATTRIBUTE_LABELS = [
  'Procedures performed',
  'Months post-op',
  'Patient age',
  'Implants used',
  'Weight/BMI'
];

export const createEmptySurgeon = (id) => ({
  id,
  name: '',
  sort_order: 0,
  enabled: 1
});

export const createEmptySection = () => ({
  name: '',
  identifier: '',
  before_image: '',
  after_image: '',
  enabled: 1,
  sort_order: 0,
  items: []
});

export const createEmptyItem = () => ({
  title: '',
  description: '',
  images: [],
  tags: '',
  surgeons: [],
  enabled: 1,
  sort_order: 0,
  attributes: []
});

export const parseJsonConfig = (value) => {
  if (!value) {
    return [];
  }

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const toFlag = (value, fallback) => {
  if (value === undefined || value === null || value === '') {
    return fallback;
  }

  return Number(value) ? 1 : 0;
};

const toEnabledFlag = (value) => (value === undefined || value === null || Number(value) ? 1 : 0);

export const configValue = (configs, path, fallback = '') => {
  const value = configs[path];
  return (value === undefined || value === null) ? fallback : String(value);
};

export const configFlag = (configs, path, fallback) => toFlag(configs[path], fallback);

export const normalizeSurgeon = (surgeon) => ({
  id: Number(surgeon?.id) || 0,
  name: surgeon?.name || '',
  sort_order: Number(surgeon?.sort_order) || 0,
  enabled: toEnabledFlag(surgeon?.enabled)
});

export const normalizeItem = (item) => {
  let images = [];

  if (Array.isArray(item?.images)) {
    images = item.images.filter(Boolean);
  } else if (item?.image) {
    images = [item.image];
  }

  const tags = Array.isArray(item?.tags) ? item.tags.join(', ') : (item?.tags || '');

  let surgeonIds = [];

  if (Array.isArray(item?.surgeons)) {
    surgeonIds = item.surgeons;
  } else if (Array.isArray(item?.surgeon_ids)) {
    surgeonIds = item.surgeon_ids;
  }

  return {
    title: item?.title || '',
    description: item?.description || '',
    images,
    tags,
    surgeons: surgeonIds.map(Number).filter(Boolean),
    enabled: toEnabledFlag(item?.enabled),
    sort_order: Number(item?.sort_order) || 0,
    attributes: Array.isArray(item?.attributes)
      ? item.attributes.map((attribute) => ({
        label: attribute?.label || '',
        value: attribute?.value || ''
      }))
      : []
  };
};

export const normalizeSection = (section) => ({
  name: section?.name || '',
  identifier: section?.identifier || '',
  before_image: section?.before_image || '',
  after_image: section?.after_image || '',
  enabled: toEnabledFlag(section?.enabled),
  sort_order: Number(section?.sort_order) || 0,
  items: (Array.isArray(section?.items) ? section.items : []).map(normalizeItem)
});

export const serializeSurgeons = (surgeons) => JSON.stringify(
  surgeons.map((surgeon) => ({
    id: Number(surgeon.id),
    name: surgeon.name.trim(),
    sort_order: Number(surgeon.sort_order) || 0,
    enabled: surgeon.enabled ? 1 : 0
  }))
);

const serializeItem = (item) => ({
  title: item.title.trim(),
  description: item.description,
  images: item.images.filter(Boolean),
  tags: item.tags.split(',').map((tag) => tag.trim()).filter(Boolean),
  surgeons: item.surgeons.map(Number).filter(Boolean),
  enabled: item.enabled ? 1 : 0,
  sort_order: Number(item.sort_order) || 0,
  attributes: item.attributes
    .filter((attribute) => attribute.label.trim() || attribute.value.trim())
    .map((attribute) => ({
      label: attribute.label.trim(),
      value: attribute.value.trim()
    }))
});

export const serializeSections = (sections) => JSON.stringify(
  sections.map((section) => ({
    name: section.name.trim(),
    identifier: section.identifier.trim(),
    before_image: section.before_image,
    after_image: section.after_image,
    enabled: section.enabled ? 1 : 0,
    sort_order: Number(section.sort_order) || 0,
    items: section.items.map(serializeItem)
  }))
);
